
import React from "react";
import useBreakpoint from "hooks/useBreakpoint";
import { BottomModal, SushiModal } from "../styles.common";

/**
 * Props accepted by BottomModal (styled `sushiweb/Modal` rendered as a bottom sheet on mobile).
 *
 * @typedef {Object} BottomModalProps
 * @property {boolean}          [visible]           - Controls whether the modal is open.
 * @property {function}         [onClose]           - Callback fired when the modal requests to close.
 * @property {boolean}          [showCloseIcon]     - Whether to render the built-in close (×) icon.
 * @property {boolean}          [overlayClickClose] - If true, clicking the overlay closes the modal.
 * @property {number}           [overlayOpacity]    - Opacity of the backdrop overlay (0–1).
 * @property {string}           [className]         - Additional CSS class applied to the modal root.
 * @property {number}           [zIndex]            - z-index of the modal stack.
 * @property {React.ReactNode}  [children]          - Content rendered inside the bottom sheet.
 */

/**
 * Props accepted by SushiModal (styled `sushiweb/Modal` rendered as a floating dialog on desktop).
 *
 * @typedef {Object} SushiModalProps
 * @property {boolean}          [visible]           - Controls whether the modal is open.
 * @property {function}         [onClose]           - Callback fired when the modal requests to close.
 * @property {boolean}          [isCentered]        - If true, the modal is vertically centred in the viewport.
 * @property {boolean}          [showCloseIcon]     - Whether to render the built-in close (×) icon.
 * @property {boolean}          [overlayClickClose] - If true, clicking the overlay closes the modal.
 * @property {number}           [overlayOpacity]    - Opacity of the backdrop overlay (0–1).
 * @property {string}           [className]         - Additional CSS class applied to the modal root.
 * @property {string}           [title]             - Optional heading rendered inside the modal header.
 * @property {number}           [zIndex]            - z-index of the modal stack.
 * @property {React.ReactNode}  [children]          - Content rendered inside the floating dialog.
 */

/**
 * ResponsiveModal renders a SushiModal (Floating modal) on desktop and a BottomModal (Bottom Sheet) on mobile.
 * Shared props are spread to both modals as a base.
 * `floatingProps` override shared props for SushiModal (desktop).
 * `bottomSheetProps` override shared props for BottomModal (mobile).
 *
 * @param {Object}          props
 * @param {boolean}         [props.visible]           - Controls whether either modal is open.
 * @param {function}        [props.onClose]           - Callback fired when the modal requests to close.
 * @param {boolean}         [props.showCloseIcon]     - Whether to render the built-in close icon (shared default).
 * @param {boolean}         [props.overlayClickClose] - If true, clicking the overlay closes the modal (shared default).
 * @param {number}          [props.overlayOpacity]    - Backdrop opacity shared across both modal variants.
 * @param {string}          [props.className]         - CSS class shared across both modal variants.
 * @param {number}          [props.zIndex]            - z-index shared across both modal variants.
 * @param {BottomModalProps} [props.bottomSheetProps] - Props applied exclusively to BottomModal (mobile), overriding shared props.
 * @param {SushiModalProps}  [props.floatingProps]    - Props applied exclusively to SushiModal (desktop), overriding shared props.
 * @param {React.ReactNode} [props.children]          - Content rendered inside whichever modal is active.
 */
export default function ResponsiveModal({ bottomSheetProps, floatingProps, children, ...sharedProps }) {
  const { isDesktop } = useBreakpoint();

  if (isDesktop) {
    return <SushiModal {...sharedProps} {...floatingProps}>{children}</SushiModal>;
  }

  return <BottomModal {...sharedProps} {...bottomSheetProps}>{children}</BottomModal>;
}
